import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import useAuth from './auth.context';
import { Subject, catchError, debounceTime, of, retry, switchMap, tap, zip } from 'rxjs';
import { AzureUserRole, UserSettingsRequest, getAzureUserRoles, getUserSettings, postUserSettings } from '../api';
import { filter, includes, isEmpty, isNil, uniq } from 'lodash';
import { useTranslation } from 'react-i18next';
import plants from './plants.json';
import { IonToast } from '@ionic/react';
import { DEFAULT_ROLES, DEFAULT_TOAST_DURATION } from '../constants';

interface SettingsProviderProps {
	children?: React.ReactNode;
}

interface StoredUser {
	language: string;
	plant: string;
	roles: string[];
	theme: ThemeOption;
}

interface SettingsContextInterface extends StoredUser {
	plants: string[];
	saveLanguage: (language: string) => void;
	savePlant: (plant: string) => void;
	saveTheme: (theme: ThemeOption) => void;
}

export type ThemeOption = 'light' | 'dark';

const SettingsContext = createContext<SettingsContextInterface>(
	{} as SettingsContextInterface
);


export function useSettingsContext() {
	return useContext(SettingsContext);
}

const ROLE_MAP = {
	"8a5cf454-67b3-4d53-bcae-178b3087bea3": "SEARCH",
	"12b794b6-fa0a-4c39-a40a-aea81bd4d4b3": "COLLECT",
	"992bf8e6-34cb-44d0-83b8-82789111d1b9": "ADMIN",
};

const DEFAULT_DEBOUNCE_TIME = 2000;

export function SettingsProvider({ children }: SettingsProviderProps) {
	const [theme, setTheme] = useState<ThemeOption>('dark');
	const [language, setLanguage] = useState<string>('en');
	const [plant, setPlant] = useState<string>(plants[0]);
	const [roles, setRoles] = useState<string[]>(DEFAULT_ROLES);
	const [updateFailed, setUpdateFailed] = useState<boolean | null>(null);
	const { i18n, t } = useTranslation();

	const updateQueue$ = useRef(new Subject<UserSettingsRequest>()).current;
	const { acquireToken, username } = useAuth();

	useEffect(() => {
		const rehydratedUser = rehydrateSettings();
		if (username) {
			acquireToken().pipe(
				switchMap(accessToken => zip(
					getUserSettings(accessToken, username!).pipe(
						tap(userSettings => {
							if (!isNil(userSettings.detail)) {
								throw new Error(userSettings.detail);
							}
						}),
						catchError(error => {
							console.error('Could not fetch user settings', error);
							return of(null);
						})
					),
					getAzureUserRoles(accessToken).pipe(
						catchError(error => {
							console.error('Could not fetch user roles', error);
							return of(null);
						})
					),
				)),
			).subscribe(([userSettings, userRoles]) => {
				if (isNil(userSettings) && isNil(userRoles)) {
					localStorage.setItem("user", JSON.stringify(rehydratedUser));
					return;
				}

				if (!isNil(userSettings)) {
					updateLanguage(userSettings.language);
					updateTheme(userSettings.theme as ThemeOption);
					updatePlant(userSettings.my_plant);
				}

				if (!isNil(userRoles)) {
					//@ts-ignore
					const mappedRoles = uniq([...mapUserRoles(userRoles), ...DEFAULT_ROLES]);
					updateRoles(mappedRoles);
				}
			});
		}
	}, [username]);

	useEffect(() => {
		const prefersDark = window.matchMedia("(prefers-color-scheme: dark)");
		prefersDark.addEventListener("change", (mediaQuery) =>
			updateTheme(mediaQuery.matches ? 'dark' : 'light')
		);

		updateQueue$.pipe(
			debounceTime(DEFAULT_DEBOUNCE_TIME),
		)
			.subscribe((settings) => {
				return acquireToken().pipe(
					switchMap(accessToken => postUserSettings(accessToken, settings)),
					retry({
						count: 2,
						delay: 4000,
					}),
				)
					.subscribe({
						next: (settings) => {
							console.log('Updated user settings', settings);
							setUpdateFailed(false);
						},
						error: (error) => {
							console.error('Could not update user settings', error);
							setUpdateFailed(true);
						}
					});
			});

		return () => {
			prefersDark.removeEventListener("change", (mediaQuery) =>
				updateTheme(mediaQuery.matches ? 'dark' : 'light')
			);
			updateQueue$.unsubscribe();
		}
	}, []);

	function rehydrateSettings(): StoredUser {
		const prefersDark = window.matchMedia("(prefers-color-scheme: dark)");

		const storedUser = localStorage.getItem("user");

		if (isNil(storedUser)) {
			toggleDarkTheme(prefersDark.matches);
			updateRoles(DEFAULT_ROLES);
			updateLanguage('en');
			updatePlant(plants[0]);
		}

		let parsedUser: StoredUser;
		const colorMode = prefersDark.matches ? 'dark' : 'light';
		try {
			parsedUser = JSON.parse(storedUser!);
			updateLanguage(parsedUser.language ?? 'en');
			updateRoles(parsedUser.roles ?? DEFAULT_ROLES);
			updateTheme(parsedUser.theme ?? colorMode);
			updatePlant(parsedUser.plant ?? plants[0]);
		} catch (e) {
			console.error('Could not parse stored user object', e);

			localStorage.removeItem("user");
			updateTheme(colorMode);
			updateLanguage('en');
			updateRoles(DEFAULT_ROLES);
			parsedUser = {
				language: 'en',
				roles: DEFAULT_ROLES,
				plant: plants[0],
				theme: colorMode,
			}
		}

		return parsedUser;
	}

	function updateTheme(theme: ThemeOption) {
		const storedUser = getStoredUser();
		setTheme(theme);
		toggleDarkTheme(theme == 'dark');
		localStorage.setItem("user", JSON.stringify({
			...storedUser,
			theme,
		}));
	}

	function updateLanguage(language: string) {
		const storedUser = getStoredUser();
		setLanguage(language);
		i18n.changeLanguage(language);
		localStorage.setItem("user", JSON.stringify({
			...storedUser,
			language,
		}));
	}

	function updatePlant(plant: string) {
		if (!includes(plants, plant)) {
			console.error('Invalid plant', plant);
			return;
		}
		setPlant(plant);
		const storedUser = getStoredUser();
		localStorage.setItem("user", JSON.stringify({
			...storedUser,
			plant,
		}));
	}

	function updateRoles(updatedRoles: string[]) {
		const uniqueRoles = uniq(updatedRoles);
		setRoles(uniqueRoles);
		const storedUser = getStoredUser();
		localStorage.setItem("user", JSON.stringify({
			...storedUser,
			roles: uniqueRoles,
		}));
	}

	function toggleDarkTheme(shouldAdd: boolean) {
		document.body.classList.toggle("dark", shouldAdd);
	}

	function mapUserRoles(roles: AzureUserRole[]): string[] {
		const recognizedRoles = filter(roles, role => includes(Object.keys(ROLE_MAP), role.id));
		//@ts-ignore
		return recognizedRoles.map(role => ROLE_MAP[role.id]);
	}

	function getStoredUser() {
		const storedUser = localStorage.getItem("user");
		if (isNil(storedUser)) {
			return {};
		}

		try {
			return JSON.parse(storedUser);
		} catch (e) {
			console.error('Could not parse stored user object', e);
			return {};
		}
	}

	function saveTheme(newTheme: ThemeOption) {
		updateTheme(newTheme);
		scheduleUpdate({
			language,
			my_plant: plant,
			theme: newTheme,
		});
	}

	function saveLanguage(newLanguage: string) {
		updateLanguage(newLanguage);
		scheduleUpdate({
			language: newLanguage,
			my_plant: plant,
			theme,
		});
	}

	function savePlant(newPlant: string) {
		updatePlant(newPlant);
		scheduleUpdate({
			language,
			my_plant: newPlant,
			theme,
		});
	}

	function scheduleUpdate(userRequest: Omit<UserSettingsRequest, 'user'>) {
		if (isEmpty(username)) {
			return;
		}
		updateQueue$.next({
			...userRequest,
			user: username!,
		});
	}

	const memo = useMemo(() => ({
		language,
		plant,
		plants,
		roles,
		theme,
		saveTheme,
		savePlant,
		saveLanguage,
	}), [
		language,
		plant,
		plants,
		roles,
		theme,
	]);

	return (
		<SettingsContext.Provider value={memo}>
			{children}
			<IonToast
				duration={DEFAULT_TOAST_DURATION}
				isOpen={updateFailed !== null}
				message={updateFailed ? t('userSettings.saveFailed') : t('userSettings.saveSuccess')}
				onDidDismiss={() => setUpdateFailed(null)}
			/>
		</SettingsContext.Provider>
	);
}
